import React from 'react';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { compose } from 'ramda';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';

import { ProfilesProps } from './definitions';
import { Card, EditorDiv, Heading, RichText } from './StyledProfiles';
import Profile from './Profile';
import PropTypes from 'prop-types';

const EditorText = compose(withEditModeNotice)(EditorDiv);

const HeadingWithDescription = (props) => {
  let { headingField, descriptionField, headingLevel } = props;

  headingLevel = headingLevel?.value || 'h2';

  return (
    <div>
      {headingField && (
        <Heading
          tag={typeof headingLevel === 'number' ? `h${headingLevel}` : headingLevel}
          field={headingField}
        />
      )}
      {descriptionField && <RichText field={descriptionField} />}
    </div>
  );
};

HeadingWithDescription.propTypes = {
  headingField: PropTypes.object,
  descriptionField: PropTypes.object,
  headingLevel: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    PropTypes.string,
    PropTypes.number
  ])
};

export const Profiles: React.FC<ProfilesProps> = ({ rendering, editMode, sitecoreContext }) => {
  let profiles = [];
  const heading = rendering?.fields?.heading;
  const headingLevel = rendering?.fields?.headingLevel;
  const description = rendering?.fields?.description;
  const hasHeading = Boolean(heading || description)

  try {
    profiles =
      rendering?.fields?.profiles
        .map(
          ({
            id,
            fields: {
              addressLine1: { value: addressLine1 },
              addressLine2: { value: addressLine2 },
              description: { value: description },
              email: { value: email },
              name: { value: name },
              phoneNumber: { value: phoneNumber },
              postcode: { value: postcode },
              role: { value: role },
              suburb: { value: suburb },
              website: { value: website }
            }
          }) => {
            const data = {
              id,
              description,
              email,
              name,
              phoneNumber,
              role,
              website,
              address: null
            };

            const addressLine3 = suburb || postcode ? [suburb, postcode].join(' ') : '';

            // render if we have something
            if (addressLine1 || addressLine2 || addressLine3) {
              data.address = (
                <React.Fragment>
                  {addressLine1 && <div>{addressLine1}</div>}
                  {addressLine2 && <div>{addressLine2}</div>}
                  {addressLine3 && <div>{addressLine3}</div>}
                </React.Fragment>
              );
            }

            return data;
          }
        )
        .filter(({ name, website, ...rest }) => (
          name !== '' &&
          (website.href !== '' ||
            Object.keys(rest)
              .map((k) => rest[k])
              .filter(Boolean).length > 0)
        )) || [];
  } catch {
    profiles = [];
  }

  if (profiles.length === 0) {
    if (editMode) {
      return (
        <EditorText
          noticeEnabled
          noticeMessage={`This component has no valid profiles associated to it, please ensure that you have selected profiles with at least a 'Name' and one other field.`}
        />
      );
    }

    return null;
  }

  return (
    <>
      {hasHeading && (
        <HeadingWithDescription
          headingField={heading}
          descriptionField={description}
          headingLevel={headingLevel}
        />
      )}
      <Card $hasHeading={hasHeading}>
        <EditorText
          noticeEnabled={editMode && profiles.length > 1}
          noticeLevel="info"
          noticeMessage="As there are multiple profiles selected, profile details will display hidden for end users, and give them the option to expand."
        >
          {profiles.map((profile, key, arr) => (
            <Profile
              key={profile.id}
              {...profile}
              showExpandOption={editMode ? false : arr.length > 1}
              hostName={sitecoreContext.siteInfo.siteBaseUrl}
            />
          ))}
        </EditorText>
      </Card>
    </>
  );
};

export default compose(withSitecoreContext(), withEditMode)(Profiles);
